import React, { useState, useRef, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Bot, Send, User, Loader2, X, Minimize2, Maximize2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';

const INITIAL_MESSAGE = {
  role: 'bot',
  content: "Hey! I'm **BugBuster**, your dedicated feedback management assistant. I have read-only access to all feedback data including screenshots.\n\nAsk me anything — trends, patterns, duplicates, summaries, or questions about specific reports."
};

export default function FeedbackBotChat({ open, onOpenChange }) {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, minimized]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    const response = await base44.functions.invoke('feedbackBotChat', {
      messages: newMessages.filter(m => m.role !== 'system').map(m => ({
        role: m.role === 'bot' ? 'assistant' : 'user',
        content: m.content
      }))
    });

    setMessages(prev => [...prev, {
      role: 'bot',
      content: response.data.response || 'Sorry, I encountered an error.'
    }]);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRestart = () => {
    setMessages([INITIAL_MESSAGE]);
    setInput('');
  };

  if (!open) return null;

  // Minimized state - just show a small bar
  if (minimized) {
    return (
      <div className="fixed bottom-20 right-6 z-[9999] w-[300px] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
        <div data-testid="fb-feedbackbotchat-clickable-div" className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 cursor-pointer" onClick={() => setMinimized(false)}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Bot className="w-3 h-3 text-purple-400" />
            </div>
            <span className="font-semibold text-sm text-white">BugBuster</span>
            {messages.length > 1 && (
              <span className="text-[10px] text-slate-500">({messages.length - 1} messages)</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setMinimized(false); }} className="text-slate-400 hover:text-slate-200 h-6 w-6">
              <Maximize2 className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onOpenChange(false); }} className="text-slate-400 hover:text-slate-200 h-6 w-6">
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-[9999] w-[420px] max-w-[calc(100vw-2rem)] h-[520px] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/50 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <span className="font-semibold text-sm text-white">BugBuster</span>
            <p className="text-[10px] text-slate-400">Feedback Assistant · Read-Only</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={handleRestart} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Restart conversation">
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setMinimized(true)} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Minimize">
            <Minimize2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-200 h-7 w-7" title="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3" ref={scrollRef}>
        <div className="space-y-3">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'bot' && (
                <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-3 h-3 text-purple-400" />
                </div>
              )}
              <div className={`max-w-[85%] min-w-0 px-3 py-2 rounded-lg text-sm ${
                msg.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-200'
              }`}>
                {msg.role === 'bot' ? (
                  <div className="prose prose-sm prose-invert max-w-none break-words overflow-wrap-anywhere [&_p]:m-0 [&_p]:break-words [&_ul]:my-1 [&_li]:my-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-all [&_code]:whitespace-pre-wrap">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-3 h-3 text-indigo-400" />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-purple-400" />
              </div>
              <div className="bg-slate-800 px-3 py-2 rounded-lg">
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t border-slate-700 shrink-0">
        <div className="flex gap-2">
          <Textarea data-testid="fb-feedbackbotchat-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask BugBuster about feedback..."
            className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 min-h-[40px] max-h-[80px] resize-none text-sm"
            disabled={loading}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 self-end"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}